import { SidePanel } from "@common/SidePanel/SidePanel";
import { ICartOffer, ICatalogOffer } from "@features/cart/ICart";
import { useHomeBroadbandSidePanel } from "@features/home/mobile/HomeBroadbandSidePanel/useHomeBroadbandSidePanel";
import { HomeBroadbandSidePanelPrequal } from "@features/home/mobile/HomeBroadbandSidePanel/HomeBroadbandSidePanelPrequal";
import { useStyle } from "./homeBroadbandSidePanelStyle";
import { ROUTES } from "@constants";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { HomeBroadbandOffer } from "@features/home/common/HomeBroadbandOffer/HomeBroadbandOffer";
import { IFetchState } from "itsf-ui-common";
import { IEirSalesPrequal } from "@modules/eirSalesFacade/interfaces/models/IEirSalesPrequal";
import { HomeBroadbandEmail } from "@features/home/common/HomeBroadbandEmail/HomeBroadbandEmail";
import { HomeBroadbandAlreadyHave } from "@features/home/common/HomeBroadbandAlreadyHave/HomeBroadbandAlreadyHave";
import broadbandOfferNotAvailable from "@static/img/home/broadbandOfferNotAvailable.png";
import broadbandOfferAlreadyHave from "@static/img/home/broadbandOfferAlreadyHave.png";
import cartImg from "@static/img/header/Cart.svg";
import {
    getIsBroadbandAlreadyHaveDisplayed,
    getIsBroadbandNotAvailableDisplayed,
    getIsBroadbandOfferDisplayed,
} from "@features/home/utils/broadbandDisplayed";
import { BroadbandAddon } from "@common/BroadbandAddon/BroadbandAddon";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import { Badge, Stack, SvgIcon, Typography } from "@mui/material";
import { useContext } from "react";
import { NumberOfferContext } from "../../../../App";

interface IParams {
    offer: ICatalogOffer;
    isOpen: boolean;
    hasBroadbandOfferInCart?: boolean;
    broadbandCartOffer?: ICartOffer;
    addBroadbandOfferState: IFetchState;
    onClose?(): void;
    handleAddBroadbandOffer(offer: ICatalogOffer, prequal: IEirSalesPrequal): void;
    handleDeleteBroadbandOffer(): void;
}

export const HomeBroadbandSidePanel = (props: IParams) => {
    const {
        offer,
        isOpen,
        onClose,
        broadbandCartOffer,
        handleAddBroadbandOffer,
        handleDeleteBroadbandOffer,
        hasBroadbandOfferInCart,
        addBroadbandOfferState,
    } = props;
    const { t } = useTranslation("home");

    const { push } = useHistory();
    const { cartItemsNumber } = useContext(NumberOfferContext);
    const {
        prequal,
        setPrequal,
        resetPrequal,
        handleChangeAddress,
        isMobile,
        offerAddonList,
    } = useHomeBroadbandSidePanel({
        handleDeleteBroadbandOffer,
        hasBroadbandOfferInCart,
        cartOfferId: broadbandCartOffer?.offerId,
    });

    const { classes } = useStyle({ isMobile });

    return (
        <SidePanel
            anchor={isMobile ? "bottom" : "right"}
            isOpen={isOpen}
            width={isMobile ? "100%" : 550}
            onClose={onClose}
        >
            <div className={classes.container}>
                {!prequal && <HomeBroadbandSidePanelPrequal offer={offer} setPrequal={setPrequal} />}
                {prequal && getIsBroadbandOfferDisplayed(prequal) && (
                    <div className={classes.broadbandOfferContainer}>
                        <HomeBroadbandOffer
                            addBroadbandOfferState={addBroadbandOfferState}
                            handleAddBroadbandOffer={handleAddBroadbandOffer}
                            handleChangeAddress={handleChangeAddress}
                            hasBroadbandOfferInCart={hasBroadbandOfferInCart}
                            isSidePanel
                            offer={offer}
                            prequal={prequal}
                        />
                        {broadbandCartOffer &&
                            offerAddonList?.map((addon) => (
                                <BroadbandAddon
                                    addon={addon}
                                    broadbandCartOffer={broadbandCartOffer}
                                    isCompactLayout
                                    isSidePanel
                                    key={addon.code}
                                />
                            ))}
                    </div>
                )}
                {prequal && getIsBroadbandNotAvailableDisplayed(prequal) && (
                    <div className={classes.section}>
                        <img alt="Broadband not available" className={classes.image} src={broadbandOfferNotAvailable} />
                        <HomeBroadbandEmail handleChangeAddress={resetPrequal} isWrapped prequal={prequal} />
                    </div>
                )}
                {prequal && getIsBroadbandAlreadyHaveDisplayed(prequal) && (
                    <div className={classes.section}>
                        <img alt="Already have broadband" className={classes.image} src={broadbandOfferAlreadyHave} />
                        <HomeBroadbandAlreadyHave handleChangeAddress={resetPrequal} />
                    </div>
                )}
                <div className={classes.footer}>
                    {isMobile && (
                        <LabeledButton
                            className={classes.button}
                            id={
                                BUTTON_IDS.MOBILE_FLOW.DRAWER_CTA[
                                    hasBroadbandOfferInCart ? "CONTINUE_TO_BASKET" : "SKIP_AND_CONTINUE_TO_CART"
                                ]
                            }
                            labelText={hasBroadbandOfferInCart ? t("goToBasket") : t("skipToBasket")}
                            onClick={() => push(ROUTES.CART_PAGE_URL)}
                        >
                            {hasBroadbandOfferInCart ? t("goToBasket") : t("skipToBasket")}
                        </LabeledButton>
                    )}
                    {!isMobile && (
                        <div className={classes.footerContent}>
                            <Stack className={classes.basketInfo} direction="row">
                                <Badge
                                    badgeContent={cartItemsNumber}
                                    className={classes.badge}
                                    invisible={!cartItemsNumber}
                                >
                                    <SvgIcon className={classes.cartIcon} component={cartImg} viewBox="0 0 33 30" />
                                </Badge>
                                <Typography className={classes.basketText} variant="large">
                                    <span className={classes.basketTextBold}>{t("yourBasket")}</span> (
                                    {t("itemAdded", { counter: cartItemsNumber })})
                                </Typography>
                            </Stack>
                            <LabeledButton
                                className={classes.button}
                                id={
                                    BUTTON_IDS.MOBILE_FLOW.DRAWER_CTA[
                                        hasBroadbandOfferInCart ? "CONTINUE_TO_BASKET" : "SKIP_AND_CONTINUE_TO_CART"
                                    ]
                                }
                                labelText={hasBroadbandOfferInCart ? t("goToBasket") : t("skipToBasket")}
                                onClick={() => push(ROUTES.CART_PAGE_URL)}
                            >
                                {hasBroadbandOfferInCart ? t("goToBasket") : t("skipToBasket")}
                            </LabeledButton>
                        </div>
                    )}
                </div>
            </div>
        </SidePanel>
    );
};
